import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ResponsiveService } from 'src/app/nucleo/utilidades/responsive.service';
import { BehaviorSubject, debounceTime, map, Subscription, tap } from 'rxjs';
import {
  stringVacío,
  valorNumérico,
} from 'src/app/compartido/utilidades/valores.utils';
import {
  BarcodeScannerLivestreamComponent,
  BarcodeScannerLivestreamModule,
} from 'ngx-barcode-scanner';
import { QuaggaJSResultObject } from '@ericblade/quagga2';
import { NgIf } from '@angular/common';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OrdenesService } from 'src/app/ordenes/utilidades/ordenes.service';

@Component({
  selector: 'compartido-lector-codigos',
  imports: [BarcodeScannerLivestreamModule, NgIf, MatProgressSpinnerModule],
  templateUrl: './lector-codigos.component.html',
  styleUrls: ['./lector-codigos.component.scss'],
})
export class LectorCodigosComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  /* Suscripción */
  private _suscripción = new Subscription();

  /* Tipo de lector */
  @Input() tipo: 'usb' | 'camara' = 'usb';

  /* Stream de valores leídos */
  @Output() valor = new EventEmitter<number>();

  /* Escáner por cámara */
  @ViewChild('escaner')
  private _escaner!: BarcodeScannerLivestreamComponent;
  protected escaneo = new BehaviorSubject<QuaggaJSResultObject | null>(null);

  /* Escáner por USB */
  private _valorUSB = '';
  private temporizadorUSB: any;

  constructor(
    protected readonly responsiveService: ResponsiveService,
    protected readonly _ordenesService: OrdenesService,
    private readonly _alertasService: AlertasService,
  ) {}

  ngOnInit(): void {
    if (this.tipo === 'camara') {
      const escaneo = this.escaneo.pipe(
        debounceTime(200),
        map((valor: QuaggaJSResultObject | null) => valor?.codeResult.code),
        tap((valor: string | null | undefined) => this.emitirValor$(valor)),
      );
      this._suscripción.add(escaneo.subscribe());
    }
  }

  ngAfterViewInit(): void {
    if (this.tipo === 'camara') this.iniciarEscaner();
  }

  ngOnDestroy(): void {
    this._suscripción.unsubscribe();
  }

  /**
   * Inicia el escáner de códigos de barras vía cámara.
   * @author Juan Corral
   */
  public iniciarEscaner(): void {
    this._escaner.start();
  }

  /**
   * Pausa el escáner de códigos de barras vía cámara
   * @author Juan Corral
   */
  public pausarEscaner(): void {
    this._escaner.stop();
  }

  /**
   * Maneja el evento de lectura de un código de barras vía USB.
   * @param {KeyboardEvent} evento - El evento de escaneo.
   * @autor Juan Corral
   */
  protected leerUSB(evento: KeyboardEvent) {
    clearTimeout(this.temporizadorUSB);

    if (evento.key === 'Enter') {
      if (this._valorUSB.length === 0) return;
      this.emitirValor$(this._valorUSB);
      this._valorUSB = '';
    } else {
      this._valorUSB += evento.key;
    }

    this.temporizadorUSB = setTimeout(() => (this._valorUSB = ''), 300);
  }

  /**
   * Emite un valor válido basado en el valor recibido.
   * @param {string | null | undefined} valor - El valor a emitir.
   * @author Juan Corral
   */
  private async emitirValor$(valor: string | null | undefined): Promise<void> {
    if (stringVacío(valor)) return;

    if (valor!.includes('-')) {
      const id = await this._obtenerOrdenId$(valor!.split('-')[0]);
      if (id !== undefined) this.valor.next(id);
    } else {
      const num = valorNumérico(valor);
      if (num === undefined)
        this._alertasService.alertarError('Código inválido');
      else this.valor.next(num);
    }
  }

  /**
   * Lee una referencia de orden y emite su ID.
   * @param {string} referencia - La referencia de la orden a buscar.
   * @return {Promise<number | undefined>} - El ID de la orden encontrada, o undefined si no se encuentra (promise).
   * @author Juan Corral
   */
  private async _obtenerOrdenId$(
    referencia: string,
  ): Promise<number | undefined> {
    const ordenes = await this._ordenesService.obtenerOrdenes$({ referencia });
    if (ordenes.count === 0) {
      this._alertasService.alertarError(
        'No se encontró ninguna orden con esa referencia',
      );
    } else if (ordenes.count > 1) {
      this._alertasService.alertarError(
        'Se encontraron múltiples órdenes con esa referencia',
      );
    } else return ordenes.results[0].id;
    return undefined;
  }
}
